// src/pages/student/StudentAttendance.jsx
import ExcelDownloadButton from '../../components/common/ExcelDownloadButton';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchStudentAttendanceReport } from '../../api/studentPortal';
import { usePortalStudentId, useIsParentViewingChild } from '../../hooks/usePortalStudentId';
import ParentPortalHeader from '../../components/parent/ParentPortalHeader';
import SessionDateTimeDisplay from '../../components/common/SessionDateTimeDisplay';
import { buildSessionDateTimeParts } from '../../utils/sessionDateTimeDisplay';
import UserLink from '../../components/common/UserLink';
import ListPageLayout, {
    ListPageHeader,
    ListSearchCard,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from '../../components/layout/ListPageLayout';
import PaginationFooter from '../../components/common/PaginationFooter';
import SortableTableHeader from '../../components/common/SortableTableHeader';
import useSortedClientPagination from '../../hooks/useSortedClientPagination';
import { studentAttendanceSortColumns } from '../../config/tableSortColumns';
import { localizeBatchName } from '../../utils/displayNames';

function useDebounced(v, d = 200) {
    const [x, setX] = useState(v);
    useEffect(() => { const t = setTimeout(() => setX(v), d); return () => clearTimeout(t); }, [v, d]);
    return x;
}

const attendanceBadge = (val, t) => {
    const s = (val || '').toUpperCase();
    const label = s
        ? t(`reports.common.${s.toLowerCase()}`, { defaultValue: val })
        : '—';
    if (s === 'PRESENT') return <span className="badge-success">{label}</span>;
    if (s === 'ABSENT') return <span className="badge-danger">{label}</span>;
    return <span className="badge-gray">{label}</span>;
};

const formatWhenForExport = (row) => {
    const start = row.startTime || row.sessionStart;
    const end = row.endTime || row.sessionEnd;
    const parts = buildSessionDateTimeParts(start, end);
    if (!parts) return start || '—';
    return [parts.date, parts.timeRange].filter(Boolean).join(' ');
};

const sessionWhenCell = (row) => (
    <SessionDateTimeDisplay
        startTime={row.startTime || row.sessionStart}
        endTime={row.endTime || row.sessionEnd}
    />
);

const StudentAttendance = () => {
    const { t } = useTranslation();
    const childStudentId = usePortalStudentId();
    const isParentView = useIsParentViewingChild();
    const title = isParentView ? t('pages.studentAttendance.parentTitle') : t('pages.studentAttendance.title');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);

    const exportColumns = useMemo(() => [
        { header: t('table.when'), key: 'startTime', render: (_, row) => formatWhenForExport(row), width: 28 },
        {
            header: t('common.status'),
            key: 'attendanceType',
            render: (v, row) => {
                const raw = v || row.status;
                if (!raw) return '—';
                return t(`reports.common.${String(raw).toLowerCase()}`, { defaultValue: raw });
            },
            width: 10,
        },
        { header: t('table.level'), key: 'levelName', width: 20 },
        {
            header: t('table.batch'),
            key: 'batchName',
            render: (v) => localizeBatchName(v, t),
            width: 22,
        },
        { header: t('table.coach'), key: 'coachName', width: 24 },
    ], [t]);

    useEffect(() => {
        let aborted = false;
        (async () => {
            setLoading(true);
            setErr('');
            try {
                const data = await fetchStudentAttendanceReport(childStudentId);
                if (!aborted) setRows(Array.isArray(data) ? data : []);
            } catch (e) {
                if (!aborted) { setRows([]); setErr(t('pages.studentAttendance.loadFailed')); }
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => { aborted = true; };
    }, [childStudentId, t]);

    const filtered = useMemo(() => {
        const term = (dq || '').trim().toLowerCase();
        if (!term) return rows || [];
        const pick = (x) => (x ?? '').toString().toLowerCase();
        return (rows || []).filter((r) =>
            [r.attendanceType || r.status, r.levelName, r.batchName, r.coachName, r.sessionTitle, r.startTime, r.endTime]
                .some((v) => pick(v).includes(term))
        );
    }, [rows, dq]);

    const pagination = useSortedClientPagination(filtered, {
        columns: studentAttendanceSortColumns,
        resetKey: dq,
        defaultSortKey: 'date',
        defaultSortDir: 'desc',
    });

    const shown = filtered.length, total = rows.length;

    if (loading) return <ListPageLayout><ListPageHeader title={title} subtitle={t('common.loading')} showBack={false} /><ListLoadingSpinner /></ListPageLayout>;
    if (err) {
        return (
            <ListPageLayout>
                <ListPageHeader title={title} subtitle={t('pages.studentAttendance.subtitle')} />
                <div className="banner-danger">{err}</div>
            </ListPageLayout>
        );
    }

    return (
        <ListPageLayout>
            <ParentPortalHeader />
            <ListPageHeader
                title={title}
                subtitle={t('pages.studentAttendance.subtitle')}
                actions={(
                    <ExcelDownloadButton
                        columns={exportColumns}
                        data={filtered}
                        filename="my-attendance"
                        disabled={filtered.length === 0}
                    />
                )}
            />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('pages.studentAttendance.searchPlaceholder')}
                shown={shown}
                total={total}
                entityLabel={t('pages.studentAttendance.entityLabel')}
            />

            {filtered.length === 0 ? (
                <ListEmptyState
                    message={q ? t('pages.studentAttendance.noMatch') : t('pages.studentAttendance.empty')}
                />
            ) : (
                <ListTableCard>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="table-header">
                                <tr>
                                    <SortableTableHeader label={t('table.when')} columnKey="when" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} align="center" />
                                    <SortableTableHeader label={t('common.status')} columnKey="status" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('table.level')} columnKey="level" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('table.batch')} columnKey="batch" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('table.coach')} columnKey="coach" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pagination.pageItems.map((r, i) => (
                                    <tr key={i} className="table-row">
                                        <td className="table-cell text-gray-700 text-center">{sessionWhenCell(r)}</td>
                                        <td className="table-cell">{attendanceBadge(r.attendanceType || r.status, t)}</td>
                                        <td className="table-cell text-gray-700">{r.levelName || '—'}</td>
                                        <td className="table-cell text-gray-700">{localizeBatchName(r.batchName, t) || '—'}</td>
                                        <td className="table-cell">
                                            {r.coachId && r.coachName ? (
                                                <UserLink id={r.coachId} name={r.coachName} />
                                            ) : (
                                                r.coachName || '—'
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pagination.total > 0 && (
                        <PaginationFooter
                            page={pagination.page}
                            size={pagination.size}
                            total={pagination.total}
                            onPageChange={pagination.setPage}
                            onSizeChange={pagination.setSize}
                        />
                    )}
                </ListTableCard>
            )}
        </ListPageLayout>
    );
};

export default StudentAttendance;
